<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { frontendURL } from 'dashboard/helper/URLHelper';
import { dynamicTime } from 'shared/helpers/timeHelper';

const store = useStore();
const route = useRoute();
const { t } = useI18n();

const CATEGORIES = [
  'prospect',
  'cold_call',
  'job_seeker',
  'solicitation',
  'support',
  'other',
];
const STATUSES = [
  'new_lead',
  'qualified',
  'contacted',
  'won',
  'lost',
  'disqualified',
];

const activeCategory = ref('');
const activeStatus = ref('');

const leads = useMapGetter('leads/getLeads');
const meta = useMapGetter('leads/getMeta');
const uiFlags = useMapGetter('leads/getUIFlags');

const isEmpty = computed(
  () => !uiFlags.value.isFetching && !leads.value.length
);

function fetchLeads() {
  store.dispatch('leads/get', {
    category: activeCategory.value,
    status: activeStatus.value,
  });
}

function conversationUrl(lead) {
  return frontendURL(
    `accounts/${route.params.accountId}/conversations/${lead.conversation_display_id}`
  );
}

function updateStatus(lead, status) {
  store.dispatch('leads/update', { id: lead.id, status });
}

watch([activeCategory, activeStatus], fetchLeads);
onMounted(fetchLeads);
</script>

<template>
  <div class="flex flex-col w-full h-full overflow-hidden bg-n-background">
    <header class="flex flex-col gap-4 px-6 pt-6 pb-4">
      <div class="flex items-center justify-between">
        <h1 class="text-xl font-medium text-n-slate-12">
          {{ t('LEADS.HEADER') }}
        </h1>
        <span class="text-sm text-n-slate-11">
          {{ t('LEADS.COUNT', { count: meta.count }) }}
        </span>
      </div>

      <div class="flex flex-wrap items-center gap-2">
        <select
          v-model="activeCategory"
          class="h-8 py-0 pl-3 pr-8 text-sm border rounded-lg bg-n-alpha-2 border-n-weak text-n-slate-12"
        >
          <option value="">{{ t('LEADS.FILTERS.ALL_CATEGORIES') }}</option>
          <option
            v-for="category in CATEGORIES"
            :key="category"
            :value="category"
          >
            {{ t(`LEADS.CATEGORY.${category.toUpperCase()}`) }}
          </option>
        </select>

        <select
          v-model="activeStatus"
          class="h-8 py-0 pl-3 pr-8 text-sm border rounded-lg bg-n-alpha-2 border-n-weak text-n-slate-12"
        >
          <option value="">{{ t('LEADS.FILTERS.ALL_STATUSES') }}</option>
          <option v-for="status in STATUSES" :key="status" :value="status">
            {{ t(`LEADS.STATUS.${status.toUpperCase()}`) }}
          </option>
        </select>
      </div>
    </header>

    <div class="flex-1 px-6 pb-6 overflow-auto">
      <p
        v-if="uiFlags.isFetching"
        class="py-10 text-sm text-center text-n-slate-11"
      >
        {{ t('LEADS.LOADING') }}
      </p>

      <p v-else-if="isEmpty" class="py-10 text-sm text-center text-n-slate-11">
        {{ t('LEADS.EMPTY') }}
      </p>

      <table v-else class="w-full text-sm border-collapse">
        <thead>
          <tr class="text-left border-b border-n-weak text-n-slate-11">
            <th class="py-2 pr-4 font-medium">{{ t('LEADS.TABLE.NAME') }}</th>
            <th class="py-2 pr-4 font-medium">
              {{ t('LEADS.TABLE.COMPANY') }}
            </th>
            <th class="py-2 pr-4 font-medium">
              {{ t('LEADS.TABLE.INTEREST') }}
            </th>
            <th class="py-2 pr-4 font-medium">
              {{ t('LEADS.TABLE.PLATFORM') }}
            </th>
            <th class="py-2 pr-4 font-medium">{{ t('LEADS.TABLE.BUDGET') }}</th>
            <th class="py-2 pr-4 font-medium">
              {{ t('LEADS.TABLE.CATEGORY') }}
            </th>
            <th class="py-2 pr-4 font-medium">{{ t('LEADS.TABLE.STATUS') }}</th>
            <th class="py-2 pr-4 font-medium">
              {{ t('LEADS.TABLE.CREATED') }}
            </th>
            <th class="py-2 font-medium" />
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="lead in leads"
            :key="lead.id"
            class="align-top border-b border-n-weak hover:bg-n-alpha-1"
          >
            <td class="py-3 pr-4">
              <span class="block font-medium text-n-slate-12">
                {{ lead.name || t('LEADS.UNKNOWN') }}
              </span>
              <span class="block text-n-slate-11">{{ lead.email }}</span>
              <span v-if="lead.phone" class="block text-n-slate-11">
                {{ lead.phone }}
              </span>
            </td>
            <td class="py-3 pr-4 text-n-slate-12">
              <span class="block">{{ lead.company }}</span>
              <span v-if="lead.website" class="block text-n-slate-11">
                {{ lead.website }}
              </span>
            </td>
            <td class="py-3 pr-4 text-n-slate-12">
              <span class="block">{{ lead.service_interest }}</span>
              <span
                v-if="lead.summary"
                class="block max-w-xs text-n-slate-11 line-clamp-2"
              >
                {{ lead.summary }}
              </span>
            </td>
            <td class="py-3 pr-4 text-n-slate-12">{{ lead.platform }}</td>
            <td class="py-3 pr-4 text-n-slate-12">
              <span class="block">{{ lead.budget }}</span>
              <span v-if="lead.timeline" class="block text-n-slate-11">
                {{ lead.timeline }}
              </span>
            </td>
            <td class="py-3 pr-4">
              <span
                class="inline-block px-2 py-0.5 text-xs rounded-md"
                :class="
                  lead.category === 'prospect'
                    ? 'bg-n-teal-3 text-n-teal-11'
                    : 'bg-n-alpha-2 text-n-slate-11'
                "
              >
                {{ t(`LEADS.CATEGORY.${lead.category.toUpperCase()}`) }}
              </span>
            </td>
            <td class="py-3 pr-4">
              <select
                :value="lead.status"
                class="h-7 py-0 pl-2 pr-6 text-xs border rounded-md bg-n-alpha-2 border-n-weak text-n-slate-12"
                @change="updateStatus(lead, $event.target.value)"
              >
                <option
                  v-for="status in STATUSES"
                  :key="status"
                  :value="status"
                >
                  {{ t(`LEADS.STATUS.${status.toUpperCase()}`) }}
                </option>
              </select>
            </td>
            <td class="py-3 pr-4 text-n-slate-11">
              {{ dynamicTime(lead.created_at) }}
            </td>
            <td class="py-3">
              <router-link
                v-if="lead.conversation_display_id"
                :to="conversationUrl(lead)"
                class="text-n-blue-11 hover:underline"
              >
                {{ t('LEADS.VIEW_CHAT') }}
              </router-link>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>
